import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { label: 'MetaTrader 4', value: 'MT4' },
  { label: 'MetaTrader 5', value: 'MT5' },
];

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState('');
  const { addItem, items } = useCart();

  useEffect(() => {
    const params = filter ? `?platform=${filter}` : '';
    axios.get(`${API}/api/products${params}`).then(r => setProducts(r.data)).catch(() => {});
  }, [filter]);

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8" data-testid="products-page">
      <div className="mx-auto max-w-7xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400/60 mb-2">TRADING ROBOTS & INDICATORS</p>
        <h1 className="text-3xl sm:text-4xl font-bold text-white">Trading Robots and Indicators</h1>
        <p className="mt-2 text-sm text-white/40">Battle-tested Expert Advisors and Indicators built for MetaTrader 4 and MetaTrader 5.</p>

        <div className="mt-8 flex gap-2" data-testid="platform-filters">
          {FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f.value ? 'bg-amber-400/10 text-amber-400 border border-amber-400/20' : 'border border-white/[0.06] text-white/50 hover:text-white hover:border-white/10'}`}
              data-testid={`filter-${f.value || 'all'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {(() => {
          const renderCard = (p) => {
            const inCart = items.some(i => i.slug === p.slug);
            return (
              <div key={p.slug} className="group rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden hover:border-amber-400/20 transition-all" data-testid={`product-card-${p.slug}`}>
                <Link to={`/products/${p.slug}`} className="block relative aspect-[16/10] overflow-hidden">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute top-3 left-3 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-400/90 text-black">{p.platform}</span>
                </Link>
                <div className="p-4">
                  <Link to={`/products/${p.slug}`}>
                    <h3 className="font-semibold text-white/90 group-hover:text-amber-400 transition-colors">{p.name}</h3>
                  </Link>
                  <p className="mt-1.5 text-xs text-white/40 line-clamp-2">{p.description}</p>
                  <ul className="mt-3 space-y-1">
                    {p.features?.slice(0, 3).map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[11px] text-white/50">
                        <span className="text-amber-400">-</span><span className="line-clamp-1">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 flex items-center justify-between">
                    <div>
                      <span className="text-xl font-bold text-white">${p.price}</span>
                      <span className="text-xs text-white/30 ml-1">USD</span>
                    </div>
                    <button
                      onClick={() => addItem(p)}
                      disabled={inCart}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${inCart ? 'bg-white/5 text-white/30' : 'bg-amber-400/10 text-amber-400 border border-amber-400/20 hover:bg-amber-400/20'}`}
                      data-testid={`add-to-cart-${p.slug}`}
                    >
                      {inCart ? 'In Cart' : 'Add to Cart'}
                    </button>
                  </div>
                </div>
              </div>
            );
          };

          const renderPlatformSection = (platform, label) => {
            const platformProducts = products.filter(p => p.platform === platform);
            const eas = platformProducts.filter(p => p.category === 'ea');
            const indicators = platformProducts.filter(p => p.category === 'indicator');
            if (eas.length === 0 && indicators.length === 0) return null;
            return (
              <div key={platform} className="mb-14" data-testid={`platform-section-${platform}`}>
                <h2 className="text-2xl font-bold text-white mb-1">{label}</h2>
                <div className="h-px bg-gradient-to-r from-amber-400/40 via-amber-400/10 to-transparent mb-6" />
                {eas.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-amber-400/80 mb-4">Expert Advisors</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {eas.map(renderCard)}
                    </div>
                  </div>
                )}
                {indicators.length > 0 && (
                  <div className="mb-2">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-amber-400/80 mb-4">Indicators</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {indicators.map(renderCard)}
                    </div>
                  </div>
                )}
              </div>
            );
          };

          return (
            <div className="mt-8">
              {(!filter || filter === 'MT4') && renderPlatformSection('MT4', 'MetaTrader 4')}
              {(!filter || filter === 'MT5') && renderPlatformSection('MT5', 'MetaTrader 5')}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
